import { registerSW } from 'virtual:pwa-register';

/**
 * The legacy app's update rule (ADR-010, docs/yashgin-pilot/design.md §2): a new version waits;
 * it is applied quietly when the user is not in the middle of something, and otherwise offered
 * in a small bar. The app looks for a new version on start, on returning to it and every 30 min.
 */

const CHECK_EVERY_MS = 30 * 60 * 1000;

class Update {
  ready = $state(false);
}
const update = new Update();
let apply: ((reload?: boolean) => Promise<void>) | null = null;

/** Work that a reload would lose (a file being imported). Dialogs and panels are seen in the DOM. */
let workInProgress = 0;

/** Marks the page as busy while `active()` is true (a reload now would lose what the user did). */
export function useWorkInProgress(active: () => boolean): void {
  $effect(() => {
    if (!active()) return;
    workInProgress += 1;
    return () => {
      workInProgress -= 1;
    };
  });
}

/** Busy: a panel or dialog is open, the user is typing, or a page holds unsaved work. */
export function isBusy(doc: Document = document): boolean {
  if (workInProgress > 0) return true;
  if (doc.querySelector('dialog[open]')) return true;
  const active = doc.activeElement;
  return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement;
}

/** Starts the service worker (production builds only; the dev server has none). */
export function startPwa(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  apply = registerSW({
    immediate: true,
    onNeedRefresh() {
      update.ready = true;
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => {
        if (navigator.onLine) registration.update().catch(() => undefined);
      };
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('online', check);
    },
  });
}

let applying = false;

/**
 * Activates the waiting version and reloads once. The plugin reloads only for what Workbox calls
 * an update, which misses a first install and its update in the same page session; so, like the
 * legacy app, the page also reloads when the new worker takes control, or after 2.5 s at most.
 */
export function applyUpdate(): void {
  if (applying) return;
  applying = true;
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  void apply?.(true);
  setTimeout(() => window.location.reload(), 2500);
}

/** Whether a new version is waiting (tests set it through `markUpdateReady`). */
export function updateReady(): boolean {
  return update.ready;
}

export function markUpdateReady(ready = true): void {
  update.ready = ready;
}

/**
 * Applies a ready version at the first safe moment: now if nothing is open, else when the user
 * comes back to the app. Call while a component initializes; read whether a version is waiting.
 */
export function quietUpdate(): { readonly ready: boolean } {
  $effect(() => {
    if (!update.ready) return;
    const tryApply = () => {
      if (!isBusy()) applyUpdate();
    };
    const timer = setTimeout(tryApply, 400);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryApply();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  });
  return {
    get ready() {
      return update.ready;
    },
  };
}
